// =============================================================================
//  Sub-company (उपकंपनी) master for the Master Report
// =============================================================================
//  A loan application "belongs to" a company through the APPLICANT'S CASTE/समाज.
//  Each company below lists the castes it covers in `castes`.
//
//  Matching is case-insensitive and a row matches if its caste value equals ANY
//  entry in `castes`. We include both English and Marathi spellings so it works
//  whichever way CasteName is stored. If your DB stores caste CODES instead of
//  names, add those codes to the arrays too.
//
//  `est` = establishment / GR date (yyyy-mm-dd). When a sub-company is selected
//  in the Master Report the From Date is pinned to this date so results are
//  restricted to records on/after the company started.
//
//  Source: official "मुख्य कंपनी आणि उपकंपन्यांची नावे / कोणासाठी" list.
//  NOTE: this list shows company #7 as 'श्री शिव गोरक्षनाथ नाथपंथी समाज'
//  (Nathpanthi). An earlier sheet showed 'परमपुज्य गंगानाथ महाराज' instead.
//  We follow this (caste) list. The est date for #7 is assumed 2025-03-04
//  (same GR batch as #4-#6) — change if you have the exact date.
// =============================================================================

export const MAIN_COMPANY = {
  id: "main",
  name: "वसंतराव नाईक विमुक्त जाती व भटक्या जमाती विकास महामंडळ (मुख्य कंपनी)",
  shortName: "VNVJNT (Main)",
  est: "1984-02-08",
  isMain: true,
  // Main company covers VJ / JNT / SBC. Empty = no caste restriction (all).
  castes: []
};

export const SUB_COMPANIES = [
  {
    id: "rajeumaji",
    name: "राजे उमाजी नाईक आर्थिक विकास महामंडळ",
    shortName: "Raje Umaji Naik",
    est: "2024-04-01",
    // कोणासाठी: रामोशी, बेरड  (DB: "VJ-A  रामोशी", "VJ-A  बेरड")
    castes: ["रामोशी", "Ramoshi", "बेरड", "Berad"]
  },
  {
    id: "vadar",
    name: "पैलवान कै. मारुती चव्हाण-वडार आर्थिक विकास महामंडळ",
    shortName: "Vadar",
    est: "2024-04-01",
    // कोणासाठी: वडार, पाथरवट, संगतराश/दगडफोडू  (DB: "VJ-A  वडार", "VJ-A  पाथरवट", "VJ-A  संगतराश")
    // गाडी/जाती/माती वड्डर are not separate rows in the DB (covered by "वडार").
    castes: [
      "वडार", "Vadar", "पाथरवट", "संगतराश",
      "वड्डर", "गाडी वड्डर", "जाती वड्डर", "माती वड्डर", "दगडफोडू", "दगडफोड"
    ]
  },
  {
    id: "divyanand",
    name: "ब्रम्हलिन आचार्य दिव्यानंद पूरीजी महाराज आर्थिक विकास महामंडळ",
    shortName: "Divyanand Maharaj",
    est: "2025-04-01",
    // कोणासाठी: लोहार व तत्सम — घिसाडी, गाडी/चितोडी/रजपूत/पांचाळ लोहार, खाती, खातवाढी, चितारी/जिनगर, चितोडीया-लोहार
    // "लोहार" (contains) covers गाडी/चितोडी/रजपूत/पांचाळ/घिसाडी/चितोडीया लोहार. DB uses "खातवाढी" (ढ).
    castes: [
      "लोहार", "घिसाडी", "खाती", "खातवाढी", "चितारी", "जिनगर",
      "चितोडीया-लोहार", "रजपूत लोहार", "पांचाळ लोहार", "गाडी लोहार", "चितोडी लोहार", "Lohar"
    ]
  },
  {
    id: "shrikrishna",
    name: "श्रीकृष्ण आर्थिक विकास महामंडळ",
    shortName: "Shrikrishna",
    est: "2025-04-01",
    // कोणासाठी: गवळी, मुस्लीम गवळी, गवलान, ग्वालवंश, गोपाल-गवळी, गवळी-गोपाल  (DB uses "गवलान" — ल)
    // "गवळी" (contains) covers मुस्लीम गवळी / गोपाल-गवळी / गवळी-गोपाल. Bare "गोपाल" avoided (matches "गोपाल भोरपी").
    castes: [
      "गवळी", "गवलान", "ग्वालवंश", "मुस्लीम गवळी",
      "गोपाल-गवळी", "गवळी-गोपाल", "Gavli", "Gawli"
    ]
  },
  {
    id: "vinkar",
    name: "विणकर समाज आर्थिक विकास महामंडळ",
    shortName: "Vinkar Samaj",
    est: "2025-04-01",
    // कोणासाठी: विणकर समाज.
    // NOTE: ObcCastes has NO "विणकर" row — weavers are recorded as साळी / कोष्टी / देवांग (all SBC).
    // This filter matches nothing until a विणकर caste exists OR those weaving castes are added here.
    castes: ["विणकर", "Vinkar"]
  },
  {
    id: "gorakshanath",
    name: "श्री शिव गोरक्षनाथ नाथपंथी समाज आर्थिक विकास महामंडळ",
    shortName: "Gorakshanath Nathpanthi",
    est: "2025-04-01",
    // कोणासाठी: नाथपंथीय समाज — नाथपंथी डवरी गोसावी, नाथजोगी, भराडी, किंगरीवाले, मरीआईवाले,
    // बहुरूपी, गोसावी, मसनजोगी(समशान जोगी), गोंधळी, डोंबारी, चित्रकथी.
    // "नाथ" covers नाथपंथी/नाथ जोगी/नाथबावा/नाथपंथी गोसावी. Bare "जोगी" avoided (would catch "जोगी बंजारा").
    castes: [
      "नाथ", "नाथपंथी", "नाथपंथी डवरी गोसावी", "डवरी",
      "भराडी", "किंगरी", "किंगरीवाले", "मरीआई", "बहुरूपी", "गोसावी",
      "मसनजोगी", "समशान जोगी", "गोंधळी", "डोंबारी", "चित्रकथी", "Nathpanthi"
    ]
  }
];

export const ALL_COMPANIES = [MAIN_COMPANY].concat(SUB_COMPANIES);

export function getCompanyById(id) {
  for (var i = 0; i < ALL_COMPANIES.length; i++) {
    if (ALL_COMPANIES[i].id === id) return ALL_COMPANIES[i];
  }
  return null;
}



// WEBPACK FOOTER //
// ./src/config/subCompanies.js